/**
 * Toast notification messages for success/error/info states.
 * Organized by feature domain for maintainability.
 */
export const notifications = {
  assignments: {
    created: "Assignment created",
    createdWithCriteriaTemplate: "Assignment created with criteria applied",
    createdButCriteriaTemplateFailed: "Assignment created, but unable to apply criteria. You can close this dialog and edit the assignment to try again.",
    updated: "Assignment updated",
    deleted: "Assignment deleted",
    archived: "Assignment archived",
    unarchived: "Assignment restored",
    permanentlyDeleted: "Assignment permanently deleted",
    createFailed: "Unable to create assignment",
    updateFailed: "Unable to update assignment",
    deleteFailed: "Unable to delete assignment",
    archiveFailed: "Unable to archive assignment",
    unarchiveFailed: "Unable to restore assignment",
    invalidWordCountRange: "Minimum word count cannot exceed maximum word count",
  },

  classes: {
    created: "Class created",
    updated: "Class updated",
    deleted: "Class deleted",
    archived: "Class archived",
    unarchived: "Class restored",
    permanentlyDeleted: "Class permanently deleted",
    createFailed: "Unable to create class",
    updateFailed: "Unable to update class",
    deleteFailed: "Unable to delete class",
  },

  students: {
    created: "Student added",
    updated: "Student updated",
    deleted: "Student deleted",
    archived: "Student archived",
    unarchived: "Student restored",
    permanentlyDeleted: "Student permanently deleted",
    createFailed: "Unable to add student",
    updateFailed: "Unable to update student",
    deleteFailed: "Unable to delete student",
    alreadyExists: "A student with this name already exists in this class",
  },

  documents: {
    created: "Paper saved",
    updated: "Paper updated",
    deleted: "Paper deleted",
    queued: "Paper highlighting started",
    queuedBulk: (count: number) => `Highlighting ${count} ${count === 1 ? 'paper' : 'papers'}`,
    requeued: "Paper re-highlighting started",
    updatedAndQueued: "Paper updated and highlighting started",
    savedAndQueued: "Paper saved and highlighting started",
    createFailed: "Unable to save paper",
    updateFailed: "Unable to update paper",
    deleteFailed: "Unable to delete paper",
    queueFailed: "Unable to start highlighting",
    alreadyQueued: "Paper already highlighting",
    editorNotReady: "Editor not ready",
    studentRequired: "Student is required",
    contentRequired: "Paper text is required",
    wordLimitExceeded: "Paper exceeds 5,000 word limit",
    saveFailed: "Unable to save paper",
  },

  share: {
    linkCreated: "Share link created and copied to clipboard",
    linkRevoked: "Share link revoked",
    linkCopied: "Link copied to clipboard",
    createFailed: "Unable to create share link",
    revokeFailed: "Unable to revoke share link",
    copyFailed: "Failed to copy link",
  },

  criteria: {
    generated: (count: number) => `Generated ${count} ${count === 1 ? 'focus area' : 'focus areas'}`,
    rulesAdded: (createdCount: number, failedCount: number) =>
      failedCount > 0
        ? `${createdCount} criteria added, ${failedCount} failed`
        : `${createdCount} criteria added successfully`,
    noRulesGenerated: "No criteria generated",
    ruleCreated: "Focus area created",
    ruleUpdated: "Focus area updated",
    ruleDeleted: "Focus area deleted",
    rulesReset: "Criteria reset to defaults",
    updated: "Focus area updated",
    deleted: "Focus area deleted",
    reset: "Criteria reset to defaults",
    saved: "Criteria saved",
    generateFailed: "Failed to generate criteria",
    ruleSaveFailed: "Failed to save focus area",
    ruleDeleteFailed: "Failed to delete focus area",
    rulesResetFailed: "Failed to reset criteria",
    updateFailed: "Unable to update focus area",
    deleteFailed: "Unable to delete focus area",
    resetFailed: "Unable to reset criteria",
  },

  voice: {
    transcriptionFailed: "Unable to transcribe audio. Please try again.",
    microphonePermissionDenied: "Microphone access denied. Please enable in browser settings.",
    recordingFailed: "Recording failed. Please try again.",
    audioTooShort: "Recording too short. Please speak for at least 2 seconds.",
    connectionFailed: "Failed to connect to transcription service. Please try again.",
    streamingFailed: "Streaming interrupted. Please try again.",
    tokenFetchFailed: "Failed to initialize streaming. Please try again.",
    recordingCancelled: "Recording cancelled",
  },

  criteriaTemplates: {
    created: "Criteria saved",
    updated: "Criteria updated",
    deleted: "Criteria deleted",
    applied: "Criteria applied",
    createFailed: "Unable to save criteria",
    updateFailed: "Unable to update criteria",
    deleteFailed: "Unable to delete criteria",
    duplicated: "Criteria template duplicated",
    duplicateFailed: "Failed to duplicate template. Please try again.",
  },

  featureFlags: {
    created: "Feature flag created",
    updated: "Feature flag updated",
    deleted: "Feature flag deleted",
    enabled: "Feature flag enabled",
    disabled: "Feature flag disabled",
    unset: "Feature flag reset to default",
    createFailed: "Unable to create feature flag",
    updateFailed: "Unable to update feature flag",
    deleteFailed: "Unable to delete feature flag",
    unsetFailed: "Unable to reset feature flag",
  },

  summary: {
    updated: "Summary updated",
    reset: "Summary reset",
    fieldReset: (field: string) => `${field} reset to Markeroo version`,
    updateFailed: "Failed to save summary",
    resetFailed: "Failed to reset field",
  },

  comments: {
    added: "Comment added successfully",
    updated: "Comment updated successfully",
    enterCommentRequired: "Please enter a comment",
    maxLengthExceeded: (maxLength: number) => `Comment must be ${maxLength} characters or less`,
    addFailed: "Failed to add comment. Please try again.",
    addFailedWithReason: (reason: string) => `Failed to add comment. ${reason}`,
    updateFailed: "Failed to update comment. Please try again.",
  },

  billing: {
    checkoutFailed: "Unable to start checkout",
    portalFailed: "Unable to open billing portal",
  },

  feedback: {
    submitted: "Feedback submitted! Thank you for helping us improve.",
    submittedWithoutFiles: "Feedback submitted without screenshots. Thank you!",
    statusUpdated: "Status updated successfully",
    deleted: "Feedback deleted",
    replySent: "Reply sent successfully",
    submitFailed: "Failed to submit feedback. Please try again.",
    uploadFailed: "Failed to upload screenshots. Would you like to submit without them?",
    allUploadsFailedButSubmitted: "All screenshots failed to upload, but your feedback was submitted.",
    someUploadsFailedButSubmitted: (failed: number, total: number) =>
      `${failed} of ${total} screenshots failed to upload, but your feedback was submitted.`,
    statusUpdateFailed: "Failed to update status",
    deleteFailed: "Failed to delete feedback",
    replyFailed: "Failed to send reply",
    onlyImagesAllowed: "Only image files are allowed (PNG, JPEG, GIF, WebP)",
    maxImagesExceeded: "Maximum 3 images allowed",
    minLengthRequired: "Please provide at least 10 characters of feedback",
  },

  auth: {
    loginRequired: "Please log in to continue",
    sessionExpired: "Your session has expired. Please log in again.",
    impersonationFailed: "Failed to impersonate user",
    stopImpersonationFailed: "Failed to stop impersonation",
    registrationSuccess: "Please check your email to verify your account.",
    registrationFailed: "Unable to create account. Please try again.",
    emailVerified: "Email verified! You are now logged in.",
    emailVerificationFailed: "Unable to verify email. The link may have expired.",
    verificationResent: "Verification email sent. Please check your inbox.",
    verificationResendFailed: "Unable to resend verification email.",
    loginFailed: "Invalid email or password.",
    passwordResetRequested: "If an account exists, a password reset link has been sent.",
    passwordResetFailed: "Unable to request password reset.",
    passwordResetSuccess: "Password reset successfully. You are now logged in.",
    passwordResetInvalid: "Invalid or expired reset link. Please request a new one.",
    passwordChanged: "Password changed successfully.",
    passwordChangeFailed: "Unable to change password.",
    passwordAdded: "Password added successfully.",
    passwordAddFailed: "Unable to add password.",
    emailUnverified: "Please verify your email address before logging in.",
    oauthDenied: "Sign-in was cancelled. Please try again if you’d like to continue.",
    oauthFailed: "Unable to sign in with Google. Please try again.",
    accountNotFound: "No account found with this email. Please create one to continue.",
    accountExists: "An account with this email already exists. Please log in instead.",
  },

  highlights: {
    removeFailed: "Failed to remove highlight. Please try again.",
    deleteCommentFailed: "Failed to delete comment. Please try again.",
  },

  clipboard: {
    copied: "Copied to clipboard",
    copyFailed: "Failed to copy to clipboard",
  },

  portal: {
    keyRegenerated: "Portal link regenerated",
    keyRegenerateFailed: "Failed to regenerate portal link",
    keyRevoked: "Portal access revoked",
    keyRevokeFailed: "Failed to revoke portal access",
  },

  settings: {
    vocabularyUpdated: "Vocabulary updated successfully",
    vocabularyUpdateFailed: "Failed to update vocabulary. Please try again.",
    vocabularyReset: "Vocabulary reset to defaults",
    vocabularyResetFailed: "Failed to reset vocabulary. Please try again.",
    wordAdded: "Word added",
    wordRemoved: "Word removed",
    wordAlreadyExists: "This word is already in the list",
  },

  account: {
    preferredNameUpdated: "Preferred name updated",
    preferredNameUpdateFailed: "Failed to update preferred name",
    analysisModeUpdated: "Highlighting mode updated",
    analysisModeUpdateFailed: "Failed to update highlighting mode",
    studentWritingUpdated: "Student writing setting updated",
    studentWritingUpdateFailed: "Failed to update student writing setting",
    deleteFailed: "Failed to delete account. Please try again.",
  },

  emailTemplates: {
    created: "Email template created",
    updated: "Email template updated",
    deleted: "Email template deleted",
    createFailed: "Unable to create email template",
    updateFailed: "Unable to update email template",
    deleteFailed: "Unable to delete email template",
    cannotDeleteDefault: "Cannot delete the default template",
    emailCopied: "Email copied to clipboard",
    copyFailed: "Failed to copy email",
  },

  generic: {
    saveSuccess: "Changes saved",
    saveError: "Unable to save changes",
    loadError: "Failed to load data",
    networkError: "Network error. Please check your connection.",
    selectClass: "Please select a class",
    selectPaperType: "Please select a paper type",
  },

  googleClassroom: {
    connected: "Connected to Google Classroom",
    disconnected: "Disconnected from Google Classroom",
    connectFailed: "Unable to connect to Google Classroom",
    disconnectFailed: "Unable to disconnect from Google Classroom",
    tokenExpired: "Google Classroom authorization expired. Please reconnect.",
    missingScopes:
      "Please grant all requested permissions to connect Google Classroom. All permissions are required for the integration to work.",
    syncComplete: (count: number) => `Synced ${count} student ${count === 1 ? 'submission' : 'submissions'}`,
    syncFailed: "Unable to sync with Google Classroom",
    rateLimited: "Too many requests. Please try again in a few minutes.",
    notLinked: "This item is not linked to Google Classroom",
    // Phase 2: Course Import
    courseImported: "Class imported from Google Classroom",
    courseImportFailed: "Unable to import class",
    coursesImported: (count: number) => `Imported ${count} ${count === 1 ? 'class' : 'classes'} from Google Classroom`,
    loadCoursesFailed: "Unable to load Google Classroom courses",
    studentsImported: (count: number) => `${count} ${count === 1 ? 'student' : 'students'} synced`,
  },

  tutorial: {
    genericError: "Something went wrong with the tutorial. The tutorial will now close so you can continue working.",
  },

  mfa: {
    setupComplete: "MFA enabled successfully",
    setupFailed: "Failed to enable MFA. Please try again.",
    verified: "MFA verified successfully",
    verifyFailed: "Invalid code. Please try again.",
    disabled: "MFA disabled",
    disableFailed: "Failed to disable MFA. Please try again.",
    recoveryCodesGenerated: "New recovery codes generated",
    recoveryCodesGenerateFailed: "Failed to generate recovery codes. Please try again.",
    sessionExpired: "Your MFA session has expired. Please verify again.",
  },
} as const;

export type NotificationString = typeof notifications;
